import React from 'react';
import { Button, message, Popover, Select, Tooltip } from 'antd';
import type { NavigateFunction, Location } from 'react-router-dom';
import {
  DownOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { APP_REPORT_EVENTS } from '../../../common/enums/appReport';
import { useAppStore } from '../../store';
import { appReporter } from '../../utils/appReporter';
import type { SidebarUpdateMeta, ProfileUpdateMeta } from '../hooks/useMainLayoutUpdate';
import marketingAvatar from '../../assets/images/marketing-avatar.png';
import leadhunterAvatar from '../../assets/images/leadhunter-avatar.png';
import contentscoutAvatar from '../../assets/images/contentscout-avatar.png';
import productspecialistAvatar from '../../assets/images/productspecialist-avatar.png';
import communitymanagerAvatar from '../../assets/images/communitymanager-avatar.png';
import creativeWriterAvatar from '../../assets/images/creativewriter-avatar.png';
import publishAvatar from '../../assets/images/publish-avatar.png';
import downloadRocketIcon from '../../assets/images/download-rocket-icon.png';
import infoIcon from '../../assets/images/info-icon.png';
import demoOffIcon from '../../assets/images/demo_off.svg';
import notificationSoundIcon from '../../assets/images/notification-sound-icon.svg';
import { isEcommerceAccountSessionExpired } from '../../utils/ecommerceAccountSession';
import { getSystemShopId } from '../../utils/shopContext';
import {
  ACCOUNT_PAYMENT_DATA_UPDATED_EVENT,
  type AccountPaymentDataUpdatedDetail,
} from '../../utils/accountPaymentEvents';
import {
  findCurrentShopContext,
  PRODUCT_SELECTION_BUYING_PATH,
  PRODUCT_SELECTION_SELF_OPERATED_PATH,
  resolveProductSelectionPath,
} from '../../utils/productSelectionRoute';
import PlatformAvatar from '../../components/shared/PlatformAvatar';
import type { EcommerceShopContext } from '../../api/ecommerceAccounts';
import {
  fetchEcommerceAccounts,
  getAccountComputeBalance,
  getAccountPaymentEntitlement,
  fetchNotifications,
  type AccountPaymentEntitlement,
  type EcommerceAccountAuthRecord,
} from '../../api';
import { fetchDemoWorkspace, type DemoScenarioOption } from '../../api/demoWorkspace';
import { loadShopContexts } from '../../store/shopContexts';
import { usePaymentModals } from '../paymentModalContext';
import aiPointsIcon from '../../assets/images/ai_points.png';

interface SidebarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
}

const sidebarItems: SidebarItem[] = [
  { key: 'lead', label: '获客助手', icon: <img src={leadhunterAvatar} alt="获客助手" className="main-layout__nav-item-img" />, path: '/lead-hunter' },
  { key: 'hot', label: '素材情报员', icon: <img src={contentscoutAvatar} alt="素材情报员" className="main-layout__nav-item-img" />, path: '/content-scout' },
  { key: 'community', label: '社群运营官', icon: <img src={communitymanagerAvatar} alt="社群运营官" className="main-layout__nav-item-img" />, path: '/community-manager' },
  { key: 'select', label: '选品专员', icon: <img src={productspecialistAvatar} alt="选品专员" className="main-layout__nav-item-img" />, path: '/product-specialist' },
  { key: 'creative_writer', label: '创意编剧', icon: <img src={creativeWriterAvatar} alt="创意编剧" className="main-layout__nav-item-img" />, path: '/creative-writer' },
  { key: 'publishing_monitor', label: '投放盯盘手', icon: <img src={publishAvatar} alt="投放盯盘手" className="main-layout__nav-item-img" />, path: '/publishing-monitor' },
  // 社群运营官、选品专员，以及其他内部页面暂时只隐藏侧边菜单入口，页面路由继续保留供内部联调使用。
];

const NOTIFICATIONS_UPDATED_EVENT = 'fluxmind:notifications-updated';
const ECOMMERCE_ACCOUNTS_UPDATED_EVENT = 'fluxmind:ecommerce-accounts-updated';

function isProductSelectionPath(pathname: string) {
  return pathname.startsWith(PRODUCT_SELECTION_SELF_OPERATED_PATH)
    || pathname.startsWith(PRODUCT_SELECTION_BUYING_PATH);
}

function renderShopSelectorOption(
  item: EcommerceShopContext,
  options?: { demo?: boolean; typeLabel?: string; name?: string }
) {
  const expired = isEcommerceAccountSessionExpired(item);
  const accountName = options?.name || item.platform_account_name || '未命名账号';

  return (
    <span className="main-layout__shop-selector-option">
      {options?.demo ? (
        <span className="main-layout__shop-selector-avatar main-layout__shop-selector-avatar--demo">N</span>
      ) : (
        <PlatformAvatar
          src={item.platform_avatar_url}
          platform={item.platform_type}
          size={20}
          alt=""
          className="main-layout__shop-selector-avatar"
        />
      )}
      <Tooltip title={accountName} placement="right">
        <span className="main-layout__shop-selector-name">
          {accountName}
        </span>
      </Tooltip>
      {options?.typeLabel ? <span className="main-layout__shop-selector-type">{options.typeLabel}</span> : null}
      {expired ? <span className="main-layout__shop-selector-status">已过期</span> : null}
    </span>
  );
}

function renderDouyinSelectorOption(item: EcommerceAccountAuthRecord) {
  const accountName = item.platform_account_name || '抖音账号';
  return (
    <span className="main-layout__shop-selector-option">
      <PlatformAvatar
        src={item.platform_avatar_url}
        platform={item.platform_type}
        size={20}
        alt=""
        className="main-layout__shop-selector-avatar"
      />
      <Tooltip title={accountName} placement="right">
        <span className="main-layout__shop-selector-name">
          {accountName}
        </span>
      </Tooltip>
      <span className="main-layout__shop-selector-type">抖音</span>
    </span>
  );
}

function formatComputeBalance(value: number | null) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return Math.max(0, numeric).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function resolveMembershipBadgeLabel(entitlement: AccountPaymentEntitlement | null) {
  if (!entitlement?.active) {
    return '购买会员';
  }
  return entitlement.planTierName?.trim()
    || entitlement.planName?.trim()
    || '会员';
}

interface SidebarProps {
  collapsed: boolean;
  location: Location;
  navigate: NavigateFunction;
  currentShopId: string;
  setCurrentShopId: (shopId: string) => void;
  sidebarUpdateMeta: SidebarUpdateMeta | null;
  profileUpdateMeta: ProfileUpdateMeta;
  showGuideEntry: boolean;
  setGuideVideoModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAboutModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLogoutModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  profilePopoverOpen: boolean;
  setProfilePopoverOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  location,
  navigate,
  currentShopId,
  setCurrentShopId,
  sidebarUpdateMeta,
  profileUpdateMeta,
  showGuideEntry,
  setGuideVideoModalOpen,
  setAboutModalOpen,
  setLogoutModalOpen,
  profilePopoverOpen,
  setProfilePopoverOpen,
  setSidebarCollapsed,
}) => {
  const { shopContexts, shopContextsLoading, appConfig } = useAppStore();
  const dataSpaceState = useAppStore((state) => state.dataSpaceState);
  const enterDemoWorkspace = useAppStore((state) => state.enterDemoWorkspace);
  const exitDemoWorkspace = useAppStore((state) => state.exitDemoWorkspace);
  const currentEcomAccountId = useAppStore((state) => state.currentEcomAccountId);
  const setCurrentEcomAccountId = useAppStore((state) => state.setCurrentEcomAccountId);
  const appName = appConfig.appName;
  const {
    openMembershipPaymentModal,
    openComputePowerDetailModal,
  } = usePaymentModals();
  const [computeBalance, setComputeBalance] = React.useState<number | null>(null);
  const [paymentEntitlement, setPaymentEntitlement] = React.useState<AccountPaymentEntitlement | null>(null);
  const [boundAccounts, setBoundAccounts] = React.useState<EcommerceAccountAuthRecord[]>([]);
  const [boundAccountsLoading, setBoundAccountsLoading] = React.useState(false);
  const mountedRef = React.useRef(false);
  const isMarketingActive = location.pathname.startsWith('/marketing-operator');
  const isSettingsActive = location.pathname.startsWith('/settings') || location.pathname.startsWith('/devices');
  const roleTextClassName = `main-layout__role-text${isMarketingActive ? ' main-layout__role-text--active' : ''}`;
  const isDemoMode = dataSpaceState.dataSpace === 'DEMO';
  const selectedShop = isDemoMode
    ? dataSpaceState.demoWorkspace?.shop
    : findCurrentShopContext(shopContexts, currentShopId);
  const hasActiveMembership = Boolean(paymentEntitlement?.active);
  const membershipBadgeLabel = resolveMembershipBadgeLabel(paymentEntitlement);
  const [demoSwitching, setDemoSwitching] = React.useState(false);
  const [demoScenarios, setDemoScenarios] = React.useState<DemoScenarioOption[]>([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = React.useState(0);
  const [accountsReloadKey, setAccountsReloadKey] = React.useState(0);
  const loadPaymentProfile = React.useCallback(async () => {
    await Promise.all([
      getAccountComputeBalance(),
      getAccountPaymentEntitlement().catch(() => null),
    ])
      .then(([balancePayload, entitlementPayload]) => {
        if (mountedRef.current) {
          setComputeBalance(balancePayload?.balance ?? 0);
          setPaymentEntitlement(entitlementPayload || null);
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          setComputeBalance(0);
          setPaymentEntitlement(null);
        }
      });
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    void loadPaymentProfile();
    return () => {
      mountedRef.current = false;
    };
  }, [loadPaymentProfile]);

  React.useEffect(() => {
    const handlePaymentDataUpdated = (event: Event) => {
      const detail = event instanceof CustomEvent
        ? event.detail as AccountPaymentDataUpdatedDetail | undefined
        : undefined;
      if (typeof detail?.computeBalance === 'number') {
        setComputeBalance(detail.computeBalance);
        return;
      }
      void loadPaymentProfile();
    };

    window.addEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
    return () => {
      window.removeEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
    };
  }, [loadPaymentProfile]);

  React.useEffect(() => {
    if (!useAppStore.getState().auth.userId.trim()) {
      return;
    }
    void loadPaymentProfile();
  }, [location.pathname, location.search, loadPaymentProfile]);

  React.useEffect(() => {
    if (!useAppStore.getState().auth.userId.trim()) {
      return;
    }
    void fetchDemoWorkspace()
      .then((workspace) => {
        setDemoScenarios((workspace.scenarios || []).filter((item) => item.enabled && item.shop));
      })
      .catch(() => {
        setDemoScenarios([]);
      });
  }, []);

  React.useEffect(() => {
    if (!useAppStore.getState().auth.userId.trim()) {
      return;
    }
    let cancelled = false;
    void fetchNotifications()
      .then((payload) => {
        if (!cancelled) {
          setNotificationUnreadCount(Number(payload?.unreadCount || 0));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotificationUnreadCount(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  React.useEffect(() => {
    const userId = useAppStore.getState().auth.userId.trim();
    if (!userId || isDemoMode) {
      setBoundAccounts([]);
      setBoundAccountsLoading(false);
      return;
    }

    let cancelled = false;
    setBoundAccountsLoading(true);
    void fetchEcommerceAccounts()
      .then((records) => {
        if (!cancelled) {
          setBoundAccounts(records ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBoundAccounts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBoundAccountsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountsReloadKey, isDemoMode, location.pathname, shopContexts.length]);

  // 没有店铺且没有选中账号时，自动选中第一个独立抖音号
  React.useEffect(() => {
    if (isDemoMode || boundAccountsLoading || currentShopId.trim() || currentEcomAccountId.trim()) {
      return;
    }
    if (shopContexts.length > 0) {
      return;
    }
    const firstStandaloneDouyin = boundAccounts.find(
      (item) => item.account_type === 'DOUYIN_ACCOUNT'
        && !(item.linked_shop_ecom_account_id || item.linked_shop_id || '').trim()
    );
    if (firstStandaloneDouyin) {
      setCurrentEcomAccountId(firstStandaloneDouyin.ecom_account_id);
    }
  }, [boundAccounts, boundAccountsLoading, currentEcomAccountId, currentShopId, isDemoMode, setCurrentEcomAccountId, shopContexts.length]);

  React.useEffect(() => {
    const handleAccountsUpdated = () => {
      setAccountsReloadKey((key) => key + 1);
    };
    window.addEventListener(ECOMMERCE_ACCOUNTS_UPDATED_EVENT, handleAccountsUpdated);
    return () => {
      window.removeEventListener(ECOMMERCE_ACCOUNTS_UPDATED_EVENT, handleAccountsUpdated);
    };
  }, []);

  React.useEffect(() => {
    const handleNotificationsUpdated = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail as { unreadCount?: number } | undefined : undefined;
      if (typeof detail?.unreadCount === 'number') {
        setNotificationUnreadCount(Math.max(0, detail.unreadCount));
      }
    };
    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    };
  }, []);

  const handleHelpClick = () => {
    setProfilePopoverOpen(false);
    setAboutModalOpen(true);
  };

  const handleLogoutClick = () => {
    setProfilePopoverOpen(false);
    setLogoutModalOpen(true);
  };

  const handleMembershipClick = () => {
    setProfilePopoverOpen(false);
    if (isDemoMode) {
      message.info('当前为演示数据模式');
      return;
    }
    openMembershipPaymentModal({
      mode: hasActiveMembership ? 'renew' : 'new',
      currentPlanExpireAt: paymentEntitlement?.effectiveUntil,
    });
  };

  const handleComputePowerClick = () => {
    setProfilePopoverOpen(false);
    if (isDemoMode) {
      message.info('当前为演示数据模式');
      return;
    }
    openComputePowerDetailModal();
  };

  const handleDemoWorkspaceSelect = async (scenario: string) => {
    if (demoSwitching) {
      return;
    }
    setDemoSwitching(true);
    try {
      const workspace = await fetchDemoWorkspace(scenario);
      if (!workspace?.enabled || !workspace.shop) {
        message.warning('演示数据暂未配置');
        return;
      }
      enterDemoWorkspace(workspace);
      message.success(`已切换到${workspace.displayName || '演示数据'}`);
      if (location.pathname.startsWith('/settings')) {
        navigate('/marketing-operator');
      }
    } catch (error) {
      console.error('Failed to switch demo workspace.', error);
      message.error('演示数据切换失败');
    } finally {
      setDemoSwitching(false);
    }
  };

  const handleEnterDefaultDemoWorkspace = async () => {
    if (demoSwitching) {
      return;
    }
    setProfilePopoverOpen(false);
    setDemoSwitching(true);
    try {
      const workspace = await fetchDemoWorkspace();
      if (!workspace?.enabled || !workspace.shop) {
        message.warning('演示数据暂未配置');
        return;
      }
      enterDemoWorkspace(workspace);
      message.success(`已切换到${workspace.displayName || '演示数据'}`);
      if (location.pathname.startsWith('/settings')) {
        navigate('/marketing-operator');
      }
    } catch (error) {
      console.error('Failed to enter default demo workspace.', error);
      message.error('演示数据切换失败');
    } finally {
      setDemoSwitching(false);
    }
  };

  const handleExitDemoMode = async () => {
    exitDemoWorkspace();
    const contexts = await loadShopContexts({ silent: true, preferFirst: true });
    message.success('已退出演示模式');
    if (contexts.length === 0 || location.pathname.startsWith('/settings')) {
      navigate('/marketing-operator');
    }
  };

  const handleShopSelectorChange = async (value: string) => {
    if (value.startsWith('__demo__:')) {
      await handleDemoWorkspaceSelect(value.replace('__demo__:', ''));
      return;
    }
    if (value.startsWith('__douyin__:')) {
      const ecomAccountId = value.replace('__douyin__:', '').trim();
      if (isDemoMode) {
        exitDemoWorkspace();
        await loadShopContexts({ silent: true });
      }
      setCurrentShopId('');
      setCurrentEcomAccountId(ecomAccountId);
      reportSidebarNavigation({
        entryKey: 'douyin_account_selector',
        entryLabel: '切换抖音账号',
        triggerArea: 'sidebar',
      });
      return;
    }
    if (isDemoMode) {
      exitDemoWorkspace();
      await loadShopContexts({ silent: true });
      message.success('已切回真实数据');
    }
    setCurrentShopId(value);
    setCurrentEcomAccountId('');
    reportSidebarNavigation({
      entryKey: 'shop_selector',
      entryLabel: selectedShop?.platform_account_name || '切换店铺',
      triggerArea: 'sidebar',
    });
  };

  const renderMembershipBadge = (className: string) => (
    <span className={`main-layout__membership-badge ${className}`}>
      <span
        className="main-layout__membership-badge-plan"
        role={isDemoMode ? undefined : 'button'}
        tabIndex={isDemoMode ? undefined : 0}
        onClick={(event) => {
          event.stopPropagation();
          handleMembershipClick();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMembershipClick();
          }
        }}
      >
        {isDemoMode ? '演示模式' : membershipBadgeLabel}
      </span>
      {!isDemoMode && hasActiveMembership ? (
        <span
          className="main-layout__membership-badge-points"
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.stopPropagation();
            handleComputePowerClick();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleComputePowerClick();
            }
          }}
        >
          <img src={aiPointsIcon} alt="" aria-hidden="true" />
          <span>{formatComputeBalance(computeBalance)}</span>
        </span>
      ) : null}
    </span>
  );

  const reportSidebarNavigation = (params: {
    entryKey: string;
    entryLabel: string;
    targetPath?: string;
    triggerArea: string;
  }) => {
    // 导航必须优先于埋点执行，避免上报链路异常时造成菜单点击无响应。
    queueMicrotask(() => {
      void appReporter.reportNavigationEvent(APP_REPORT_EVENTS.SIDEBAR_NAV_CLICK, params);
    });
  };

  const navigateBySidebar = (path: string, params: {
    entryKey: string;
    entryLabel: string;
    triggerArea: string;
  }) => {
    navigate(path);
    reportSidebarNavigation({
      ...params,
      targetPath: path,
    });
  };

  const renderProfilePopoverContent = () => (
    <div className="main-layout__profile-menu">
      <div className="main-layout__profile-menu-header">
        <div className="main-layout__profile-menu-avatar">N</div>
        <div className="main-layout__profile-menu-info">
          <span className="main-layout__profile-menu-name">{appName}</span>
          {renderMembershipBadge('main-layout__profile-menu-tag')}
        </div>
      </div>
      <div className="main-layout__profile-menu-actions">
        <Button type="text" className="main-layout__profile-menu-action" onClick={handleHelpClick}>
          关于我们
        </Button>
        <Button
          type="text"
          className="main-layout__profile-menu-action"
          onClick={() => {
            reportSidebarNavigation({
              entryKey: 'guide',
              entryLabel: '使用说明',
              triggerArea: 'profile_popover',
            });
            setProfilePopoverOpen(false);
            setGuideVideoModalOpen(true);
          }}
        >
          使用说明
        </Button>
        <Button
          type="text"
          className={`main-layout__profile-menu-action main-layout__profile-menu-action--update${profileUpdateMeta.clickable ? ' main-layout__profile-menu-action--update-clickable' : ''}`}
          onClick={profileUpdateMeta.onClick}
          disabled={!profileUpdateMeta.clickable}
        >
          <span className="main-layout__profile-menu-action-label">{profileUpdateMeta.label}</span>
          {profileUpdateMeta.loading ? (
            <LoadingOutlined className="main-layout__profile-menu-action-status-icon" spin />
          ) : profileUpdateMeta.actionText ? (
            <span className="main-layout__profile-menu-action-link">{profileUpdateMeta.actionText}</span>
          ) : null}
        </Button>
        <Button
          type="text"
          className="main-layout__profile-menu-action"
          loading={demoSwitching}
          onClick={() => {
            void handleEnterDefaultDemoWorkspace();
          }}
        >
          演示模式
        </Button>
        <Button type="text" className="main-layout__profile-menu-action" onClick={handleLogoutClick}>
          退出登录
        </Button>
      </div>
    </div>
  );

  const renderBrandTrigger = () => (
    <Popover
      trigger={['hover']}
      placement="bottomLeft"
      arrow={false}
      open={profilePopoverOpen}
      onOpenChange={setProfilePopoverOpen}
      mouseEnterDelay={0.08}
      mouseLeaveDelay={0.1}
      overlayClassName="main-layout__profile-menu-popover"
      content={renderProfilePopoverContent()}
    >
      <div className="main-layout__brand-trigger" aria-label="打开账号菜单">
        <div className="main-layout__brand-section">
          <div className="main-layout__brand-avatar">N</div>
          <div className="main-layout__brand-info">
            <span className="main-layout__brand-name">{appName}</span>
            {renderMembershipBadge('main-layout__brand-tag')}
          </div>
        </div>
      </div>
    </Popover>
  );

  const renderOverviewEntry = () => (
    <Tooltip title={collapsed ? '数据总览' : undefined} placement="right">
      <button
        type="button"
        className={`main-layout__role-card${isMarketingActive ? ' main-layout__role-card--active' : ''}`}
        onClick={() => {
          navigateBySidebar('/marketing-operator', {
            entryKey: 'marketing_operator',
            entryLabel: '数据总览',
            triggerArea: 'sidebar',
          });
        }}
      >
        <span className="main-layout__role-icon">
          <img src={marketingAvatar} alt="数据总览" className="main-layout__role-img" />
        </span>
        <span className={roleTextClassName}>数据总览</span>
      </button>
    </Tooltip>
  );

  const renderShopSelector = () => {
    const currentDemoScenario = dataSpaceState.demoWorkspace?.scenario
      || dataSpaceState.demoWorkspace?.datasetId
      || 'self_operated';
    const demoOptions = demoScenarios.map((item) => ({
      value: `__demo__:${item.scenario}`,
      label: renderShopSelectorOption({
        ...(item.shop as EcommerceShopContext),
        platform_account_name: item.displayName || item.shop?.platform_account_name || '演示数据',
      }, { demo: true, typeLabel: '演示' }),
    }));
    const hasCurrentDemoOption = demoOptions.some((item) => item.value === `__demo__:${currentDemoScenario}`);
    const normalizedOptions = isDemoMode
      ? [
          ...(hasCurrentDemoOption || !dataSpaceState.demoWorkspace?.shop ? [] : [{
            value: `__demo__:${currentDemoScenario}`,
            label: renderShopSelectorOption({
              ...dataSpaceState.demoWorkspace.shop,
              platform_account_name: dataSpaceState.demoWorkspace.displayName
                || dataSpaceState.demoWorkspace.shop.platform_account_name
                || '演示数据',
            }, { demo: true, typeLabel: '演示' }),
          }]),
          ...demoOptions,
        ]
      : [
          ...shopContexts
            .map((item) => ({
              value: getSystemShopId(item),
              label: renderShopSelectorOption(item, {
                typeLabel: item.business_type === 'BUYING' ? '橱窗' : '店铺',
              }),
            }))
            .filter((item) => item.value),
          // 只展示未绑定店铺/橱窗的抖音号，已绑定的通过店铺条目选择
          ...boundAccounts
            .filter((item) => item.account_type === 'DOUYIN_ACCOUNT')
            .filter((item) => {
              const linkedShopId = (item.linked_shop_ecom_account_id || item.linked_shop_id || '').trim();
              return !linkedShopId;
            })
            .map((item) => ({
              value: `__douyin__:${item.ecom_account_id}`,
              label: renderDouyinSelectorOption(item),
            })),
        ];
    const selectorOptions = normalizedOptions;

    if (!isDemoMode && selectorOptions.length === 0) {
      return null;
    }

    // 如果当前选中的抖音号已绑定店铺，映射到所绑定的店铺值
    const userSelectedValue = (() => {
      if (currentShopId.trim()) return currentShopId.trim();
      const ecomId = currentEcomAccountId.trim();
      if (!ecomId) return undefined;
      const selectedDouyin = boundAccounts.find(
        (a) => a.account_type === 'DOUYIN_ACCOUNT' && a.ecom_account_id === ecomId
      );
      const linkedShopId = (selectedDouyin?.linked_shop_ecom_account_id || selectedDouyin?.linked_shop_id || '').trim();
      if (linkedShopId) {
        const linkedShop = shopContexts.find(
          (ctx) => ctx.ecom_account_id === linkedShopId || getSystemShopId(ctx) === linkedShopId
        );
        if (linkedShop) return getSystemShopId(linkedShop);
      }
      return `__douyin__:${ecomId}`;
    })();

    return (
      <div className="main-layout__shop-context">
        <Select
          value={isDemoMode ? `__demo__:${currentDemoScenario}` : userSelectedValue}
          className="main-layout__shop-context-select"
          loading={shopContextsLoading || boundAccountsLoading || demoSwitching}
          placeholder="选择账号"
          suffixIcon={<DownOutlined />}
          popupClassName="main-layout__shop-context-popup"
          optionLabelProp="label"
          options={selectorOptions}
          onChange={(value) => {
            void handleShopSelectorChange(value);
          }}
        />
      </div>
    );
  };

  const currentStandaloneDouyinAccount = boundAccounts.find(
    (item) => item.account_type === 'DOUYIN_ACCOUNT'
      && item.ecom_account_id === currentEcomAccountId.trim()
      && !(item.linked_shop_ecom_account_id || item.linked_shop_id || '').trim()
  );

  const renderCollapsedShopIndicator = () => {
    if (!isDemoMode && shopContexts.length === 0 && boundAccounts.length === 0) {
      return null;
    }
    const title = isDemoMode
      ? dataSpaceState.demoWorkspace?.displayName || selectedShop?.platform_account_name || '演示模式'
      : selectedShop?.platform_account_name || currentStandaloneDouyinAccount?.platform_account_name || '账号';
    const label = isDemoMode ? '演' : selectedShop ? '店' : '抖';
    return (
      <Tooltip title={title} placement="right">
        <div className="main-layout__collapsed-shop-indicator">
          {label}
        </div>
      </Tooltip>
    );
  };

  const renderNavItem = (item: SidebarItem) => {
    const targetPath = item.key === 'select'
      ? resolveProductSelectionPath(selectedShop)
      : item.path;
    const active = item.key === 'select'
      ? isProductSelectionPath(location.pathname)
      : Boolean(targetPath && location.pathname.startsWith(targetPath));

    if (targetPath) {
      return (
        <Tooltip key={item.key} title={collapsed ? item.label : undefined} placement="right">
          <button
            type="button"
            className={`main-layout__nav-item main-layout__nav-item--clickable${active ? ' main-layout__nav-item--active' : ''}`}
            onClick={() => {
              navigateBySidebar(targetPath, {
                entryKey: item.key,
                entryLabel: item.label,
                triggerArea: 'sidebar',
              });
            }}
          >
            <span className="main-layout__nav-item-icon">{item.icon}</span>
            <span className="main-layout__nav-item-label">{item.label}</span>
          </button>
        </Tooltip>
      );
    }

    return (
      <Tooltip key={item.key} title={collapsed ? item.label : undefined} placement="right">
        <button
          type="button"
          className="main-layout__nav-item main-layout__nav-item--clickable"
        >
          <span className="main-layout__nav-item-icon">{item.icon}</span>
          <span className="main-layout__nav-item-label">{item.label}</span>
        </button>
      </Tooltip>
    );
  };

  const renderSidebarNav = () => (
    <nav className="main-layout__nav" aria-label="侧边导航">
      {sidebarItems.map(renderNavItem)}
    </nav>
  );

  const renderGuideEntry = () => (
    !showGuideEntry ? null : (
    <Tooltip title={collapsed ? '使用说明' : undefined} placement="right">
      <button
        type="button"
        className="main-layout__bottom-card main-layout__bottom-card--clickable main-layout__bottom-card--guide"
        onClick={() => {
          reportSidebarNavigation({
            entryKey: 'guide',
            entryLabel: '使用说明',
            triggerArea: 'sidebar',
          });
          setGuideVideoModalOpen(true);
        }}
      >
        <span className="main-layout__bottom-card-icon">
          <img src={infoIcon} alt="使用说明" className="main-layout__bottom-card-icon-img" />
        </span>
        <span className="main-layout__bottom-card-text">使用说明</span>
      </button>
    </Tooltip>
    )
  );

  const renderNotificationEntry = () => {
    const active = location.pathname.startsWith('/notifications');
    const unreadText = notificationUnreadCount > 99 ? '99+' : String(notificationUnreadCount);
    return (
      <Tooltip title={collapsed ? '通知' : undefined} placement="right">
        <button
          type="button"
          className={`main-layout__bottom-card main-layout__bottom-card--clickable main-layout__bottom-card--notification${active ? ' main-layout__bottom-card--active' : ''}${notificationUnreadCount > 0 ? ' main-layout__bottom-card--notification-unread' : ''}`}
          onClick={() => {
            navigateBySidebar('/notifications', {
              entryKey: 'notifications',
              entryLabel: '通知',
              triggerArea: 'sidebar',
            });
          }}
        >
          <span className="main-layout__bottom-card-icon main-layout__notification-entry-icon">
            <img src={notificationSoundIcon} alt="" aria-hidden="true" className="main-layout__bottom-card-icon-img" />
          </span>
          <span className="main-layout__bottom-card-text">通知</span>
          {notificationUnreadCount > 0 ? (
            <span className="main-layout__notification-entry-badge">{unreadText}</span>
          ) : null}
        </button>
      </Tooltip>
    );
  };

  const renderSettingsEntry = () => (
    isDemoMode ? (
    <Tooltip title={collapsed ? '退出演示模式' : undefined} placement="right">
      <button
        type="button"
        className="main-layout__bottom-card main-layout__bottom-card--clickable main-layout__bottom-card--demo-exit"
        onClick={() => {
          void handleExitDemoMode();
        }}
      >
        <span className="main-layout__bottom-card-icon">
          <img src={demoOffIcon} alt="" aria-hidden="true" className="main-layout__bottom-card-icon-img" />
        </span>
        <span className="main-layout__bottom-card-text">退出演示模式</span>
      </button>
    </Tooltip>
    ) : (
    <Tooltip title={collapsed ? '设置' : undefined} placement="right">
      <button
        type="button"
        className={`main-layout__bottom-card main-layout__bottom-card--clickable main-layout__bottom-card--settings${isSettingsActive ? ' main-layout__bottom-card--active' : ''}`}
        onClick={() => {
          navigateBySidebar('/settings/accounts', {
            entryKey: 'settings',
            entryLabel: '设置',
            triggerArea: 'sidebar',
          });
        }}
      >
        <span className="main-layout__bottom-card-icon">
          <SettingOutlined />
        </span>
        <span className="main-layout__bottom-card-text">设置</span>
      </button>
    </Tooltip>
    )
  );

  const renderUpdateEntry = () => {
    if (!sidebarUpdateMeta) {
      return null;
    }

    return (
      <Tooltip title={collapsed ? sidebarUpdateMeta.tooltip : undefined} placement="right">
        <button
          type="button"
          className={`main-layout__bottom-card main-layout__bottom-card--clickable main-layout__bottom-card--update ${sidebarUpdateMeta.statusClassName}`}
          onClick={sidebarUpdateMeta.onClick}
          disabled={sidebarUpdateMeta.disabled}
          aria-label={sidebarUpdateMeta.ariaLabel}
        >
          <span className="main-layout__update-entry-rocket" aria-hidden="true">
            <img src={downloadRocketIcon} alt="" className="main-layout__update-entry-rocket-image" />
          </span>
          <span className="main-layout__update-entry-content">
            <span className="main-layout__update-entry-label">
              {sidebarUpdateMeta.label}
            </span>
            {sidebarUpdateMeta.rightIcon ? (
              <span className="main-layout__update-entry-status-icon" aria-hidden="true">
                {sidebarUpdateMeta.rightIcon}
              </span>
            ) : null}
          </span>
        </button>
      </Tooltip>
    );
  };

  const renderCollapseButton = () => (
    <button
      type="button"
      className="main-layout__collapse"
      onClick={() => setSidebarCollapsed(!collapsed)}
      aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
    >
      {collapsed ? <RightOutlined /> : <LeftOutlined />}
    </button>
  );

  return (
    <aside className={`main-layout__sidebar${collapsed ? ' main-layout__sidebar--collapsed' : ''}`}>
      {renderBrandTrigger()}
      {collapsed ? renderCollapsedShopIndicator() : renderShopSelector()}
      {renderOverviewEntry()}
      <div className="main-layout__nav-divider" />
      {renderSidebarNav()}
      <div className="main-layout__sidebar-spacer" />
      {renderUpdateEntry()}
      {renderGuideEntry()}
      {renderNotificationEntry()}
      {renderSettingsEntry()}
      {renderCollapseButton()}
    </aside>
  );
};
